// Tiện ích dùng chung cho các màn Tài liệu: quyền, người dùng hiện tại, nhãn trạng thái, định dạng, xuất Excel
import { useMemo } from 'react';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { FileText, FileSpreadsheet, FileImage, FileArchive, Presentation, File as FileIcon } from 'lucide-react';
import useAuthStore from 'store/authStore';
import { EMPLOYEES, ME_ID, blockName, deptName, issuerName, meEmployee, type Doc, type DocCategory, type DocState, type DocType, type Employee } from './data';
import { categoryPath, docState, recipientIds, type Viewer } from './logic';
import useDocsStore, { type Actor, type DocRole } from './store';

export const BRAND = { primary: '#3e91ab', button: '#357f96', dark: '#2c6b7f', soft: '#ecf6f9' };

export const currentUserName = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.fullname || u.name || (u.email ? String(u.email).split('@')[0] : '') || 'Tôi';
  } catch {
    return 'Tôi';
  }
};

export type DocAction = 'view' | 'create' | 'edit' | 'delete' | 'approve' | 'export' | 'config';

const ROLE_ACTIONS: Record<DocRole, DocAction[]> = {
  staff: ['view', 'create'],
  manager: ['view', 'create', 'edit', 'delete', 'approve', 'export'],
  admin: ['view', 'create', 'edit', 'delete', 'approve', 'export', 'config'],
};
export const ROLE_LABEL: Record<DocRole, string> = { staff: 'Nhân viên', manager: 'Quản lý tài liệu', admin: 'Admin' };

/**
 * Quyền module Tài liệu của người đang đăng nhập (document.*). Super admin toàn quyền.
 * Môi trường dev cho phép giả lập vai trò để thử luồng nhân viên / người duyệt.
 */
export const useDocAuth = () => {
  const { isSuperAdmin, permissions } = useAuthStore();
  const demoRole = useDocsStore(s => s.demoRole);
  const realRole: DocRole = isSuperAdmin || permissions.includes('document.config')
    ? 'admin'
    : permissions.includes('document.approve') || permissions.includes('document.edit') ? 'manager' : 'staff';
  const can = (action: DocAction) =>
    demoRole ? ROLE_ACTIONS[demoRole].includes(action) : isSuperAdmin || permissions.includes(`document.${action}`);
  const name = currentUserName();
  const me: Actor = { id: ME_ID, name };
  const viewer: Viewer = { id: ME_ID, manage: can('edit'), approve: can('approve') };
  return { role: demoRole || realRole, realRole, isDemo: !!demoRole, can, me, viewer };
};

/** Toàn bộ nhân sự (mock) gồm cả người đang đăng nhập */
export const usePeople = (): Employee[] => {
  const name = currentUserName();
  return useMemo(() => [meEmployee(name), ...EMPLOYEES], [name]);
};

export const personName = (people: Employee[], id: string) => people.find(p => p.id === id)?.name || '—';

export const STATE_META: Record<DocState, { label: string; cls: string; dot: string }> = {
  draft: { label: 'Dự thảo', cls: 'bg-slate-50 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
  pending: { label: 'Chờ duyệt', cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  rejected: { label: 'Bị từ chối', cls: 'bg-red-50 text-red-600 border-red-200', dot: 'bg-red-500' },
  upcoming: { label: 'Chưa hiệu lực', cls: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500' },
  active: { label: 'Còn hiệu lực', cls: 'bg-green-50 text-green-700 border-green-200', dot: 'bg-green-500' },
  expired: { label: 'Hết hiệu lực', cls: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-400' },
  replaced: { label: 'Bị thay thế', cls: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500' },
  revoked: { label: 'Đã thu hồi', cls: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
};

export const StatePill = ({ doc }: { doc: Doc }) => {
  const m = STATE_META[docState(doc)];
  return <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}><span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />{m.label}</span>;
};

export const SeenPill = ({ seen }: { seen: boolean }) =>
  seen
    ? <span className="inline-flex whitespace-nowrap rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-500">Đã xem</span>
    : <span className="inline-flex whitespace-nowrap rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600">Chưa xem</span>;

export const fmtDate = (d?: string | null) => (d ? dayjs(d).format('DD/MM/YYYY') : '—');
export const fmtDateTime = (d?: string | null) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—');
export const fmtEffective = (d: Pick<Doc, 'effectiveFrom' | 'effectiveTo'>) =>
  d.effectiveTo ? `${fmtDate(d.effectiveFrom)} – ${fmtDate(d.effectiveTo)}` : `Từ ${fmtDate(d.effectiveFrom)}`;

export const EXT_GROUPS: { key: string; label: string; exts: string[]; color: string; Icon: typeof FileText }[] = [
  { key: 'pdf', label: 'PDF', exts: ['pdf'], color: 'text-red-500', Icon: FileText },
  { key: 'doc', label: 'DOC', exts: ['doc', 'docx'], color: 'text-blue-500', Icon: FileText },
  { key: 'img', label: 'Ảnh', exts: ['png', 'jpg', 'jpeg'], color: 'text-yellow-500', Icon: FileImage },
  { key: 'ppt', label: 'PPTX', exts: ['ppt', 'pptx'], color: 'text-orange-500', Icon: Presentation },
  { key: 'xls', label: 'XLS', exts: ['xls', 'xlsx'], color: 'text-green-600', Icon: FileSpreadsheet },
  { key: 'zip', label: 'ZIP/RAR', exts: ['zip', 'rar'], color: 'text-amber-600', Icon: FileArchive },
];

export const FileTypeIcon = ({ ext, size = 18 }: { ext: string; size?: number }) => {
  const g = EXT_GROUPS.find(x => x.exts.includes(ext));
  const Icon = g?.Icon || FileIcon;
  return <Icon size={size} className={`shrink-0 ${g?.color || 'text-slate-400'}`} />;
};

export function exportDocsExcel(docs: Doc[], types: DocType[], categories: DocCategory[], people: Employee[], fileName: string) {
  const rows = docs.map((d, i) => ({
    STT: i + 1,
    'Mã tài liệu': d.code,
    'Số tài liệu': d.number,
    'Tên tài liệu': d.title,
    'Loại tài liệu': types.find(t => t.id === d.typeId)?.name || '',
    'Danh mục': categoryPath(categories, d.categoryId),
    'Khối/nghiệp vụ': blockName(d.blockId),
    'Đơn vị ban hành': deptName(d.unitId),
    'Chủ thể ban hành': issuerName(d.issuerId),
    'Người ký': personName(people, d.signerId),
    'Hiệu lực từ': fmtDate(d.effectiveFrom),
    'Hiệu lực đến': d.effectiveTo ? fmtDate(d.effectiveTo) : '',
    'Trạng thái': STATE_META[docState(d)].label,
    'Số người được chia sẻ': recipientIds(d, people).length,
    'Đã xem': Object.keys(d.views).length,
    'Số file': d.files.length,
    'Người tạo': d.createdByName,
    'Ngày tạo': fmtDateTime(d.createdAt),
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Tai lieu');
  XLSX.writeFile(wb, fileName);
}

export const docLink = (id: string) => `/personal-documents/detail?id=${encodeURIComponent(id)}`;
export const shareUrl = (id: string) => `${window.location.origin}${docLink(id)}`;
